import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Home, X, RotateCcw } from 'lucide-react';
import { playTibetanBowl, playWimhofInhale, playWimhofExhale, playWimhofSlowBreath, playMetronomeTick, speakInstruction, speakCountdown, initVoices } from '@/utils/audioUtils';
import ExerciseCopyright from './ExerciseCopyright';
import ExerciseTopLogo from './ExerciseTopLogo';

const WimHofModal = ({ 
  isOpen, 
  onClose, 
  onSessionComplete 
}) => {
  // Session states
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Phases: ready, pre-countdown, breathing, retention, recovery-inhale, recovery-hold, recovery-exhale, countdown, complete
  const [phase, setPhase] = useState('ready');
  const [breathPhase, setBreathPhase] = useState('inhale'); // inhale, exhale (during breathing phase)
  const [progress, setProgress] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Pre-start countdown (3-2-1 before beginning)
  const [preCountdown, setPreCountdown] = useState(3);
  
  // Breath count during breathing phase
  const [breathCount, setBreathCount] = useState(0);
  
  // Capsule fill level (0-100)
  const [fillLevel, setFillLevel] = useState(0);
  
  // Settings
  const [breathsPerRound, setBreathsPerRound] = useState(30); // 30 or 40 breaths
  const [totalRounds, setTotalRounds] = useState(3); // 1-10 rounds
  const [currentRound, setCurrentRound] = useState(1);
  
  // Retention timer - now counts DOWN from target
  const [retentionTime, setRetentionTime] = useState(0); // Current countdown value
  const [retentionTarget, setRetentionTarget] = useState(0); // Target time for current round
  const [retentionElapsed, setRetentionElapsed] = useState(0); // Actual time held (for stats)
  const [recoveryHoldTime, setRecoveryHoldTime] = useState(15); // Recovery hold duration
  const [recoveryTimeRemaining, setRecoveryTimeRemaining] = useState(15);
  
  // Countdown before next round
  const [countdownTime, setCountdownTime] = useState(3);
  
  // Best retention time for this session
  const [bestRetention, setBestRetention] = useState(0);
  
  // Array to store retention times for each round (actual times held)
  const [retentionTimes, setRetentionTimes] = useState([]);
  
  // Predefined retention times: 1st cycle 1'45 (105s), 2nd 2'15 (135s), +30s each
  const getTargetRetentionTime = (round) => {
    if (round === 1) return 105; // 1'45
    if (round === 2) return 135; // 2'15
    return 135 + (round - 2) * 30; // +30s for each subsequent round
  };
  
  // Breathing rhythm mode: 'fast' (1.5s/1.5s) or 'slow' (2.5s/3.5s)
  const [rhythmMode, setRhythmMode] = useState('fast');
  
  // Breathing speed (ms per breath cycle) - depends on rhythm mode
  const inhaleDuration = rhythmMode === 'fast' ? 1500 : 2500;
  const holdAfterInhale = rhythmMode === 'fast' ? 500 : 500; // 0.5s pause at top
  const exhaleDuration = rhythmMode === 'fast' ? 1500 : 3500; // Fast: 1.5s, Slow: 3.5s
  const breathCycleDuration = inhaleDuration + holdAfterInhale + exhaleDuration;
  
  // Refs
  const soundEnabledRef = useRef(true);
  const breathCountRef = useRef(0);
  const phaseRef = useRef('ready');
  const rhythmModeRef = useRef('fast');
  const transitioningToRetentionRef = useRef(false);
  
  // Keep refs in sync
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);
  
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  
  // Keep rhythm mode ref in sync
  useEffect(() => {
    rhythmModeRef.current = rhythmMode;
  }, [rhythmMode]);
  
  // Keep breath count ref in sync
  useEffect(() => {
    breathCountRef.current = breathCount;
  }, [breathCount]);
  
  // Initialize voices on mount
  useEffect(() => {
    initVoices();
    const timeout = setTimeout(() => initVoices(), 100);
    return () => clearTimeout(timeout);
  }, []);
  
  // Main exercise logic
  useEffect(() => {
    if (!isOpen || !isActive || isPaused) return;
    
    let interval;
    
    if (phase === 'pre-countdown') {
      // 3-2-1 countdown before starting
      // Play first tick immediately
      if (soundEnabledRef.current) {
        playMetronomeTick();
      }
      
      interval = setInterval(() => {
        setPreCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            // Start breathing phase
            setPhase('breathing');
            setBreathPhase('inhale');
            // Play initial inhale sound - use slow combined audio in slow mode
            if (soundEnabledRef.current) {
              if (rhythmModeRef.current === 'slow') {
                playWimhofSlowBreath(0.7);
              } else {
                playWimhofInhale(0.7);
              }
            }
            return 3; // Reset for next time
          }
          // Play metronome tick each second
          if (soundEnabledRef.current) {
            playMetronomeTick();
          }
          return prev - 1;
        });
      }, 1000);
      
    } else if (phase === 'breathing') {
      // Power breathing phase - deep breaths
      let cycleElapsed = 0;
      let currentBreathPhase = 'inhale';
      let exhalePlayedThisCycle = false;
      
      interval = setInterval(() => {
        cycleElapsed += 50;
        
        if (cycleElapsed <= inhaleDuration) {
          // Inhale - fill capsule to 100%
          const inhaleProgress = cycleElapsed / inhaleDuration;
          setFillLevel(inhaleProgress * 100);
          if (currentBreathPhase !== 'inhale') {
            currentBreathPhase = 'inhale';
            setBreathPhase('inhale');
            exhalePlayedThisCycle = false; // Reset for next exhale
          }
        } else if (cycleElapsed <= inhaleDuration + holdAfterInhale) {
          // Hold at top - capsule stays at 100%
          setFillLevel(100);
          if (currentBreathPhase !== 'hold') {
            currentBreathPhase = 'hold';
            setBreathPhase('inhale'); // Still show as inhale phase visually
          }
        } else if (cycleElapsed <= breathCycleDuration) {
          // Exhale - empty to ~20% (passive exhale, not fully empty)
          const exhaleProgress = (cycleElapsed - inhaleDuration - holdAfterInhale) / exhaleDuration;
          setFillLevel(100 - (exhaleProgress * 80));
          if (currentBreathPhase !== 'exhale') {
            currentBreathPhase = 'exhale';
            setBreathPhase('exhale');
            // Play exhale sound immediately when exhale starts - ONLY in fast mode
            // In slow mode, the combined audio already includes the exhale
            if (soundEnabledRef.current && !exhalePlayedThisCycle && rhythmModeRef.current === 'fast') {
              playWimhofExhale(0.7);
              exhalePlayedThisCycle = true;
            }
          }
        }
        
        // Complete one breath cycle
        if (cycleElapsed >= breathCycleDuration) {
          cycleElapsed = 0;
          currentBreathPhase = 'inhale';
          exhalePlayedThisCycle = false;
          
          // Calculate new count using ref to avoid double-play from React 18 Strict Mode
          const newCount = breathCountRef.current + 1;
          setBreathCount(newCount);
          
          // Voice countdown for last 3 breaths - use louder speakCountdown
          if (soundEnabledRef.current) {
            const remaining = breathsPerRound - newCount;
            if (remaining === 2) {
              // Announce the number (e.g., "28" for 30 breaths)
              speakCountdown(String(newCount), 'fr-FR');
            } else if (remaining === 1) {
              // Announce the number (e.g., "29" for 30 breaths)
              speakCountdown(String(newCount), 'fr-FR');
            } else if (remaining === 0) {
              // Announce "Dernière"
              speakCountdown('Dernière', 'fr-FR');
            }
          }
          
          // Play inhale sound for next breath (outside state updater to prevent double-play)
          // In slow mode, play combined audio. In fast mode, play separate inhale.
          // Play for ALL breaths including the last one
          if (soundEnabledRef.current && newCount <= breathsPerRound) {
            if (rhythmModeRef.current === 'slow') {
              playWimhofSlowBreath(0.7);
            } else {
              playWimhofInhale(0.7);
            }
          }
          
          // Mark that we've reached the last breath - but DON'T clear interval yet
          // Let the full cycle (inhale + hold + exhale) complete with animation and sounds
          if (newCount >= breathsPerRound && !transitioningToRetentionRef.current) {
            transitioningToRetentionRef.current = true; // Prevent multiple calls
            // Set a flag to transition after this cycle completes
            setTimeout(() => {
              clearInterval(interval);
              setFillLevel(0); // Empty lungs for retention
              // Set target retention time for this round
              const target = getTargetRetentionTime(currentRound);
              setRetentionTarget(target);
              setRetentionTime(target); // Start countdown from target
              setRetentionElapsed(0); // Reset elapsed counter
              // Wait 0.5s after exhale before announcing "Apnée" and starting retention
              setTimeout(() => {
                setPhase('retention');
                // Announce "Apnée" and play bell sound
                if (soundEnabledRef.current) {
                  speakCountdown('Apnée', 'fr-FR');
                  playTibetanBowl('low');
                }
                transitioningToRetentionRef.current = false; // Reset flag
              }, 500);
            }, breathCycleDuration); // Wait for full breath cycle (inhale + hold + exhale)
          }
        }
      }, 50);
      
    } else if (phase === 'retention') {
      // Breath hold - countdown from target time
      interval = setInterval(() => {
        setRetentionElapsed(prev => prev + 1); // Track actual time held
        setRetentionTime(prev => {
          if (prev <= 1) {
            // Countdown complete - automatically transition to recovery
            clearInterval(interval);
            // Save actual retention time (full target)
            const actualTime = retentionTarget;
            setRetentionTimes(prevTimes => [...prevTimes, actualTime]);
            if (actualTime > bestRetention) {
              setBestRetention(actualTime);
            }
            setPhase('recovery-inhale');
            if (soundEnabledRef.current) {
              playTibetanBowl('high');
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } else if (phase === 'recovery-inhale') {
      // Deep recovery breath in
      let inhaleProgress = 0;
      const recoveryInhaleDuration = 3000; // 3 seconds for deep inhale
      
      // Play inhale sound at start of recovery breath
      if (soundEnabledRef.current) {
        playWimhofInhale(0.7);
      }
      
      interval = setInterval(() => {
        inhaleProgress += 50;
        const progress = inhaleProgress / recoveryInhaleDuration;
        setFillLevel(progress * 100);
        
        if (inhaleProgress >= recoveryInhaleDuration) {
          clearInterval(interval);
          setPhase('recovery-hold');
          setRecoveryTimeRemaining(recoveryHoldTime);
          if (soundEnabledRef.current) {
            playTibetanBowl('high');
          }
        }
      }, 50);
      
    } else if (phase === 'recovery-hold') {
      // Hold after recovery breath
      interval = setInterval(() => {
        setRecoveryTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            // Go to recovery exhale phase
            setPhase('recovery-exhale');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } else if (phase === 'recovery-exhale') {
      // Exhale after recovery hold (2 seconds)
      let exhaleProgress = 0;
      const recoveryExhaleDuration = 2000; // 2 seconds
      
      // Play exhale sound
      if (soundEnabledRef.current) {
        playWimhofExhale(0.7);
      }
      
      interval = setInterval(() => {
        exhaleProgress += 50;
        const progress = exhaleProgress / recoveryExhaleDuration;
        setFillLevel(100 - (progress * 100)); // Empty from 100 to 0
        
        if (exhaleProgress >= recoveryExhaleDuration) {
          clearInterval(interval);
          setFillLevel(0);
          
          // Check if there are more rounds
          if (currentRound < totalRounds) {
            // Start countdown phase
            setPhase('countdown');
            setCountdownTime(3);
          } else {
            setPhase('complete');
            if (soundEnabledRef.current) {
              playTibetanBowl('low');
            }
          }
        }
      }, 50);
      
    } else if (phase === 'countdown') {
      // 3 second countdown with metronome before next round
      // Play first tick immediately
      if (soundEnabledRef.current) {
        playMetronomeTick();
      }
      
      interval = setInterval(() => {
        setCountdownTime(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            // Start next round
            setCurrentRound(r => r + 1);
            setPhase('breathing');
            setBreathPhase('inhale');
            setBreathCount(0);
            breathCountRef.current = 0;
            setFillLevel(0);
            // Play initial inhale sound for new round
            if (soundEnabledRef.current) {
              if (rhythmModeRef.current === 'slow') {
                playWimhofSlowBreath(0.7);
              } else {
                playWimhofInhale(0.7);
              }
            }
            return 3;
          }
          // Play metronome tick each second
          if (soundEnabledRef.current) {
            playMetronomeTick();
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isOpen, isActive, isPaused, phase, breathsPerRound, totalRounds, currentRound, recoveryHoldTime]);
  
  // Track total elapsed time
  useEffect(() => {
    if (!isOpen || !isActive || isPaused || phase === 'ready' || phase === 'complete') return;
    
    const interval = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen, isActive, isPaused, phase]);

  const startSession = () => {
    setElapsedTime(0);
    setIsActive(true);
    setIsPaused(false);
    setPhase('pre-countdown'); // Start with 3-2-1 countdown
    setPreCountdown(3);
    setBreathPhase('inhale');
    setProgress(0);
    setBreathCount(0);
    breathCountRef.current = 0; // Reset ref too
    transitioningToRetentionRef.current = false; // Reset transition flag
    setFillLevel(0);
    setRetentionTime(0);
    setRetentionTarget(0);
    setRetentionElapsed(0);
    setCurrentRound(1);
    setBestRetention(0);
    setRetentionTimes([]); // Reset retention times array
    setRecoveryTimeRemaining(recoveryHoldTime);
    // No initial sound - will play after pre-countdown
  };

  const stopSession = useCallback((saveSession = false) => {
    if (saveSession && onSessionComplete && elapsedTime > 10) {
      onSessionComplete({
        patternId: 'wim-hof',
        patternName: 'Respiration Wim Hof',
        durationSeconds: elapsedTime,
        cyclesCompleted: currentRound,
        retentionScores: retentionTimes, // Array of retention times per round
        bestRetention: bestRetention
      });
    }
    
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setBreathPhase('inhale');
    setProgress(0);
    setBreathCount(0);
    breathCountRef.current = 0; // Reset ref too
    setElapsedTime(0);
    setFillLevel(0);
    setRetentionTime(0);
    setRetentionTarget(0);
    setRetentionElapsed(0);
    setCurrentRound(1);
    setRecoveryTimeRemaining(recoveryHoldTime);
  }, [onSessionComplete, elapsedTime, currentRound, recoveryHoldTime, retentionTimes, bestRetention]);

  const handleClose = () => {
    stopSession(elapsedTime > 10);
    onClose();
  };
  
  // End retention manually (user clicked "J'ai besoin de respirer")
  const endRetention = () => {
    if (phase === 'retention') {
      // Save actual retention time held (elapsed, not remaining)
      const actualTimeHeld = retentionElapsed;
      setRetentionTimes(prev => [...prev, actualTimeHeld]);
      // Save best retention
      if (actualTimeHeld > bestRetention) {
        setBestRetention(actualTimeHeld);
      }
      setPhase('recovery-inhale');
      if (soundEnabledRef.current) {
        playTibetanBowl('high');
      }
    }
  };

  // Get phase instruction text
  const getPhaseText = () => {
    switch (phase) {
      case 'pre-countdown':
        return 'Préparez-vous...';
      case 'breathing':
        return breathPhase === 'inhale' ? 'Inspirez profondément' : 'Relâchez';
      case 'retention':
        return 'Retenez (poumons vides)';
      case 'recovery-inhale':
        return 'Grande inspiration';
      case 'recovery-hold':
        return 'Retenez';
      case 'recovery-exhale':
        return 'Expirez lentement';
      case 'countdown':
        return `Prochain cycle dans ${countdownTime}...`;
      case 'complete':
        return 'Session terminée !';
      default:
        return 'Prêt';
    }
  };
  
  // Reset session (go back to settings)
  const resetSession = () => {
    setIsActive(false);
    setIsPaused(false);
    setPhase('ready');
    setBreathPhase('inhale');
    setProgress(0);
    setBreathCount(0);
    breathCountRef.current = 0;
    transitioningToRetentionRef.current = false; // Reset transition flag
    setElapsedTime(0);
    setFillLevel(0);
    setRetentionTime(0);
    setRetentionTarget(0);
    setRetentionElapsed(0);
    setCurrentRound(1);
    setBestRetention(0);
    setRetentionTimes([]);
    setRecoveryTimeRemaining(recoveryHoldTime);
    setCountdownTime(3);
    setPreCountdown(3);
  };
  
  // Format time as mm:ss
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-hidden"
      style={{ 
        background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 30%, #2b6cb0 60%, #3182ce 100%)'
      }}
    >
      <ExerciseCopyright />
      {/* Floating Navigation Buttons */}
      <div className="absolute top-4 left-4 z-20">
        <button
          onClick={handleClose}
          className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
          title="Retour aux exercices"
        >
          <Home size={20} className="text-white" />
        </button>
      </div>
      
      <div className="absolute top-4 right-4 z-20">
        <button
          onClick={handleClose}
          className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
          title="Fermer"
        >
          <X size={20} className="text-white" />
        </button>
      </div>

        <ExerciseTopLogo />

      {/* Timer / Info - Below the Play/Pause buttons on mobile, top center on desktop */}
      {phase !== 'ready' && phase !== 'complete' && (
        <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-20 max-w-[85%] lg:bottom-auto lg:top-4 lg:max-w-none">
          <div className="bg-white/20 backdrop-blur-md rounded-2xl px-6 py-2 text-center">
            {phase === 'breathing' && (
              <div className="flex flex-col items-center">
                {totalRounds > 1 && (
                  <span className="text-xs text-white/70 uppercase tracking-wide mb-1">
                    Round {currentRound}/{totalRounds}
                  </span>
                )}
                <span className="text-2xl sm:text-3xl font-bold text-white">
                  {breathCount}/{breathsPerRound}
                </span>
                <span className="text-sm text-white/80">respirations</span>
              </div>
            )}
            {phase === 'pre-countdown' && (
              <div className="flex flex-col items-center">
                <span className="text-xs text-white/70 uppercase tracking-wide mb-1">
                  Préparez-vous
                </span>
                <span className="text-3xl sm:text-4xl font-bold text-white animate-pulse" style={{ fontFamily: 'system-ui' }}>
                  {preCountdown}
                </span>
              </div>
            )}
            {phase === 'retention' && (
              <div className="flex flex-col items-center">
                <span className="text-xs text-white/70 uppercase tracking-wide mb-1">
                  Rétention {totalRounds > 1 ? `(Round ${currentRound}/${totalRounds})` : ''}
                </span>
                <span className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'system-ui' }}>
                  {formatTime(retentionTime)}
                </span>
                <span className="text-xs text-white/60 mt-0.5">
                  Objectif: {formatTime(retentionTarget)} • Tenu: {formatTime(retentionElapsed)}
                </span>
                {bestRetention > 0 && (
                  <span className="text-xs text-white/50 mt-0.5">
                    Record: {formatTime(bestRetention)}
                  </span>
                )}
              </div>
            )}
            {phase === 'recovery-inhale' && (
              <div className="flex flex-col items-center">
                <span className="text-lg sm:text-xl font-bold text-white">
                  Inspirez à fond
                </span>
                {totalRounds > 1 && (
                  <span className="text-xs text-white/70 mt-0.5">
                    Round {currentRound}/{totalRounds}
                  </span>
                )}
              </div>
            )}
            {phase === 'recovery-hold' && (
              <div className="flex flex-col items-center">
                <span className="text-xs text-white/70 uppercase tracking-wide mb-1">
                  Récupération
                </span>
                <span className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'system-ui' }}>
                  {recoveryTimeRemaining}s
                </span>
              </div>
            )}
            {phase === 'recovery-exhale' && (
              <div className="flex flex-col items-center">
                <span className="text-lg sm:text-xl font-bold text-white">
                  Expirez lentement
                </span>
                {totalRounds > 1 && (
                  <span className="text-xs text-white/70 mt-0.5">
                    Round {currentRound}/{totalRounds}
                  </span>
                )}
              </div>
            )}
            {phase === 'countdown' && (
              <div className="flex flex-col items-center">
                <span className="text-xs text-white/70 uppercase tracking-wide mb-1">
                  Prochain round
                </span>
                <span className="text-3xl sm:text-4xl font-bold text-white animate-pulse" style={{ fontFamily: 'system-ui' }}>
                  {countdownTime}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 w-full h-full overflow-y-auto px-4 pt-20 sm:pt-24 pb-8 flex flex-col lg:flex-row lg:items-center lg:justify-center lg:pb-8">
        <div className="flex flex-col items-center my-auto gap-6 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-10 lg:items-center w-full">
          
          {/* Left Panel - Settings */}
          <div className="w-full max-w-xs lg:w-72 flex-shrink-0 order-3 lg:order-none lg:col-start-1 lg:row-start-1 lg:justify-self-end">
            {(!isActive || isPaused || phase === 'complete') && (
              <div className="bg-white/90 backdrop-blur rounded-2xl px-4 sm:px-6 py-4 sm:py-5 shadow-lg">
                <h3 className="font-semibold text-slate-700 mb-3">
                  {phase === 'complete' ? 'Session terminée !' : isPaused ? 'Pause - Réglages' : 'Réglages'}
                </h3>
                
                {phase === 'complete' && (
                  <div className="mb-4 p-3 bg-blue-100 rounded-xl">
                    <p className="text-blue-700 font-medium text-center">
                      {totalRounds} round{totalRounds > 1 ? 's' : ''} complété{totalRounds > 1 ? 's' : ''}
                    </p>
                    {bestRetention > 0 && (
                      <p className="text-blue-600 text-sm mt-1 text-center">
                        Meilleure rétention: {formatTime(bestRetention)}
                      </p>
                    )}
                    {retentionTimes.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                      <p className="text-blue-700 text-xs font-medium mb-2">Temps d&apos;apnée par cycle :</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {retentionTimes.map((time, index) => (
                            <div key={index} className="flex items-center justify-between bg-white/60 rounded-lg px-2 py-1">
                              <span className="text-blue-600 text-xs">Cycle {index + 1}</span>
                              <span className="text-blue-800 font-mono text-sm font-medium">{formatTime(time)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex flex-col gap-4 sm:gap-5">
                  {/* Breaths per round */}
                  <div className="flex flex-col gap-2">
                    <span className="text-slate-600 font-medium text-sm sm:text-base">Respirations / round</span>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[15, 20, 25, 30, 35, 40].map((num) => (
                        <button
                          key={num}
                          onClick={() => setBreathsPerRound(num)}
                          className={`px-2 py-2 rounded-xl text-sm font-medium transition-all ${
                            breathsPerRound === num
                              ? 'bg-blue-500 text-white shadow-md'
                              : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Recovery hold duration */}
                  <div className="flex flex-col gap-2">
                    <span className="text-slate-600 font-medium text-sm sm:text-base">Récupération</span>
                    <div className="flex gap-2">
                      {[10, 15, 20].map((sec) => (
                        <button
                          key={sec}
                          onClick={() => setRecoveryHoldTime(sec)}
                          className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                            recoveryHoldTime === sec
                              ? 'bg-blue-500 text-white shadow-md'
                              : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                          }`}
                        >
                          {sec}s
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Number of Rounds */}
                  <div className="flex flex-col gap-2">
                    <span className="text-slate-600 font-medium text-sm sm:text-base">Nombre de rounds</span>
                    <div className="grid grid-cols-5 gap-1.5">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                        <button
                          key={num}
                          onClick={() => setTotalRounds(num)}
                          className={`px-2 py-2 rounded-xl text-sm font-medium transition-all ${
                            totalRounds === num
                              ? 'bg-indigo-500 text-white shadow-md'
                              : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Breathing Rhythm */}
                  <div className="flex flex-col gap-2">
                    <span className="text-slate-600 font-medium text-sm sm:text-base">Rythme respiratoire</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setRhythmMode('fast')}
                        className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium transition-all ${
                          rhythmMode === 'fast'
                            ? 'bg-orange-500 text-white shadow-md'
                            : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                        }`}
                      >
                        Rapide
                        <span className="block text-[10px] opacity-80">1.5s / 1.5s</span>
                      </button>
                      <button
                        onClick={() => setRhythmMode('slow')}
                        className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium transition-all ${
                          rhythmMode === 'slow'
                            ? 'bg-teal-500 text-white shadow-md'
                            : 'bg-white/70 text-slate-600 hover:bg-white border border-slate-200'
                        }`}
                      >
                        Lent
                        <span className="block text-[10px] opacity-80">2.5s / 3.5s</span>
                      </button>
                    </div>
                  </div>
                </div>
                
                {phase === 'complete' && (
                  <button
                    onClick={startSession}
                    className="w-full mt-4 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl transition-colors"
                  >
                    Recommencer
                  </button>
                )}
              </div>
            )}
            
            {/* Retention instruction panel */}
            {phase === 'retention' && (
              <div className="bg-white/90 backdrop-blur rounded-2xl px-4 sm:px-6 py-4 sm:py-5 shadow-lg text-center">
                <p className="text-slate-600 text-sm mb-4">
                  Retenez votre souffle aussi longtemps que possible. Appuyez sur le bouton quand vous avez besoin de respirer.
                </p>
                <button
                  onClick={endRetention}
                  className="w-full px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl transition-colors"
                  data-testid="end-retention-btn"
                >
                  J&apos;ai besoin de respirer
                </button>
              </div>
            )}
          </div>

          {/* Center Column - Capsule Visualization */}
          <div className="flex flex-col items-center order-1 lg:order-none lg:col-start-2 lg:row-start-1">
            {/* Single Capsule */}
            <div className="flex flex-col items-center">
              <div 
                className={`relative w-24 sm:w-28 h-64 sm:h-80 rounded-full overflow-hidden backdrop-blur-sm border-2 shadow-2xl transition-all duration-300 ${
                  phase === 'retention'
                    ? 'bg-white/5 border-amber-400/60'
                    : 'bg-white/15 border-blue-400/60'
                }`}
              >
                {/* Background glow effect */}
                <div 
                  className="absolute inset-0 rounded-full opacity-40 transition-all duration-300"
                  style={{
                    background: phase === 'retention'
                      ? 'radial-gradient(ellipse at center bottom, rgba(251, 191, 36, 0.4), transparent 70%)'
                      : 'radial-gradient(ellipse at center bottom, rgba(59, 130, 246, 0.4), transparent 70%)'
                  }}
                />
                
                {/* Animated fill */}
                <div 
                  className="absolute bottom-0 left-0 right-0 rounded-b-full transition-all ease-out"
                  style={{
                    height: `${fillLevel}%`,
                    background: phase === 'retention' 
                      ? 'linear-gradient(to top, #d97706, #f59e0b, #fbbf24)'
                      : 'linear-gradient(to top, #1d4ed8, #3b82f6, #60a5fa)',
                    boxShadow: phase === 'retention'
                      ? '0 0 30px rgba(251, 191, 36, 0.5), inset 0 0 20px rgba(251, 191, 36, 0.3)'
                      : '0 0 30px rgba(59, 130, 246, 0.5), inset 0 0 20px rgba(59, 130, 246, 0.3)',
                    transitionDuration: phase === 'breathing' ? '50ms' : '100ms'
                  }}
                >
                  {/* Bubbles inside */}
                  {fillLevel > 10 && (
                    <div className="absolute inset-0 overflow-hidden rounded-b-full">
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute rounded-full bg-white/40 animate-pulse"
                          style={{
                            width: `${4 + (i % 3) * 2}px`,
                            height: `${4 + (i % 3) * 2}px`,
                            left: `${15 + (i * 15)}%`,
                            bottom: `${10 + (i * 18)}%`,
                            animationDelay: `${i * 0.15}s`,
                            animationDuration: `${0.8 + (i % 2) * 0.3}s`
                          }}
                        />
                      ))}
                    </div>
                  )}
                  
                  {/* Top surface shine */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-2 rounded-full bg-white/50 blur-sm" />
                </div>
                
                {/* Glass reflection overlay */}
                <div 
                  className="absolute inset-0 rounded-full pointer-events-none"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)'
                  }}
                />
                
                {/* Side highlight */}
                <div className="absolute left-2 top-4 bottom-4 w-2.5 rounded-full bg-white/15 blur-sm" />
                
                {/* Measurement markers */}
                <div className="absolute right-3 top-4 bottom-4 flex flex-col justify-between">
                  {[...Array(5)].map((_, i) => (
                    <div 
                      key={i}
                      className="w-2 h-0.5 bg-white/30 rounded-full"
                    />
                  ))}
                </div>
                
                {/* Active glow ring */}
                {phase === 'breathing' && breathPhase === 'inhale' && (
                  <div 
                    className="absolute -inset-1 rounded-full animate-pulse"
                    style={{
                      boxShadow: '0 0 25px rgba(59, 130, 246, 0.7), 0 0 50px rgba(59, 130, 246, 0.4)'
                    }}
                  />
                )}
                
                {/* Retention pulsing glow */}
                {phase === 'retention' && (
                  <div 
                    className="absolute -inset-1 rounded-full animate-pulse"
                    style={{
                      boxShadow: '0 0 25px rgba(251, 191, 36, 0.7), 0 0 50px rgba(251, 191, 36, 0.4)',
                      animationDuration: '2s'
                    }}
                  />
                )}
              </div>
            </div>

            {/* Phase indicator */}
            <div className="mt-6 bg-white/80 backdrop-blur rounded-xl px-6 py-3 shadow-lg text-center max-w-xs">
              <span className={`text-lg sm:text-xl font-bold ${
                phase === 'retention' ? 'text-amber-600' : 'text-blue-600'
              }`}>
                {getPhaseText()}
              </span>
              {isActive && phase !== 'complete' && phase !== 'retention' && (
                <div className="text-sm text-slate-500 mt-1">
                  {phase === 'breathing' && `${breathsPerRound} respirations`}
                  {phase === 'recovery-inhale' && 'Remplissez vos poumons'}
                  {phase === 'recovery-hold' && 'Poumons pleins'}
                </div>
              )}
            </div>
            
            {/* Instructions (when not active) */}
            {!isActive && phase !== 'complete' && (
              <div className="mt-4 bg-white/70 backdrop-blur rounded-xl px-4 py-3 shadow-md max-w-sm text-center">
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                  <strong>Méthode Wim Hof</strong> : {breathsPerRound} respirations profondes, 
                  puis rétention poumons vides aussi longtemps que possible, 
                  suivie d&apos;une inspiration de récupération.
                </p>
              </div>
            )}
          </div>

          {/* Right Panel - Controls */}
          <div className="w-full max-w-xs lg:w-64 flex-shrink-0 flex justify-center lg:justify-start order-2 lg:order-none lg:col-start-3 lg:row-start-1 lg:justify-self-start">
            <div className="flex flex-row lg:flex-col gap-3 sm:gap-4 exercise-controls-mobile-fixed">
              <button
                onClick={() => {
                  if (!isActive || phase === 'complete') {
                    startSession();
                  } else {
                    setIsPaused(!isPaused);
                  }
                }}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(59, 130, 246, 0.5)' }}
                data-testid="wimhof-play-btn"
              >
                {!isActive || isPaused || phase === 'complete' ? (
                  <Play size={28} className="text-slate-600 ml-1 sm:w-9 sm:h-9" />
                ) : (
                  <Pause size={28} className="text-slate-600 sm:w-9 sm:h-9" />
                )}
              </button>

              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                style={{ border: '3px solid rgba(59, 130, 246, 0.5)' }}
                data-testid="wimhof-sound-btn"
              >
                {soundEnabled ? (
                  <Volume2 size={22} className="text-slate-600 sm:w-7 sm:h-7" />
                ) : (
                  <VolumeX size={22} className="text-slate-600 sm:w-7 sm:h-7" />
                )}
              </button>
              
              {/* Reset button - only show when active */}
              {isActive && phase !== 'ready' && (
                <button
                  onClick={resetSession}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/80 backdrop-blur shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  style={{ border: '3px solid rgba(239, 68, 68, 0.5)' }}
                  data-testid="wimhof-reset-btn"
                  title="Réinitialiser"
                >
                  <RotateCcw size={22} className="text-red-500 sm:w-7 sm:h-7" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WimHofModal;
